/**
 * Posts the contact form to the CRM public intake endpoint and renders its
 * per-field errors. The form keeps a real `action`, so if this script never
 * runs the browser still submits and the lead is not lost.
 *
 * The endpoint answers { ok: true } or { ok: false, errors: { <field>: msg } },
 * with "_form" for form-level errors. Its messages are Spanish-only, so we
 * render our own localised strings and only fall back to the server text for
 * keys we do not know about.
 */
class ContactForm {
  constructor(form) {
    this.form = form;
    this.submit = form.querySelector('[data-contact-form-submit]');
    this.formError = form.querySelector('[data-contact-form-error]');
    this.success = document.getElementById(form.dataset.successTarget);
    this.strings = this.readStrings();
    this.sending = false;

    this.form.addEventListener('submit', this.onSubmit.bind(this));
    this.form.addEventListener('input', this.onInput.bind(this));
  }

  readStrings() {
    const node = document.getElementById(this.form.dataset.stringsTarget);
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return {};
    }
  }

  async onSubmit(event) {
    event.preventDefault();
    if (this.sending) return;

    this.clearErrors();
    this.setSending(true);

    try {
      const response = await fetch(this.form.action, {
        method: 'POST',
        body: new FormData(this.form),
      });

      // A body we cannot parse still tells us nothing useful, so treat it as a failure.
      const payload = await response.json().catch(() => null);

      if (response.ok && payload && payload.ok) {
        this.showSuccess();
        return;
      }

      this.resetCaptcha();

      if (payload && payload.errors) {
        this.showErrors(payload.errors, response.status);
      } else {
        this.showFormError(this.messageForStatus(response.status));
      }
    } catch (error) {
      // Network down, blocked by CORS, or the request never left the browser.
      this.resetCaptcha();
      this.showFormError(this.strings.error_network);
    } finally {
      this.setSending(false);
    }
  }

  /** Clear a field's error as soon as the visitor edits it. */
  onInput(event) {
    const field = event.target.closest('[data-contact-form-field]');
    if (!field || !field.classList.contains('c-contact-form__field--invalid')) return;

    field.classList.remove('c-contact-form__field--invalid');
    const message = field.querySelector('[data-error-for]');
    if (message) message.textContent = '';
    event.target.removeAttribute('aria-invalid');
  }

  setSending(sending) {
    this.sending = sending;
    if (!this.submit) return;

    this.submit.disabled = sending;
    this.submit.setAttribute('aria-busy', String(sending));
    this.submit.textContent = sending ? this.strings.sending : this.strings.submit;
  }

  clearErrors() {
    if (this.formError) this.formError.textContent = '';

    this.form.querySelectorAll('[data-contact-form-field]').forEach((field) => {
      field.classList.remove('c-contact-form__field--invalid');
      const message = field.querySelector('[data-error-for]');
      if (message) message.textContent = '';
    });

    this.form.querySelectorAll('[aria-invalid]').forEach((input) => {
      input.removeAttribute('aria-invalid');
    });
  }

  showErrors(errors, status) {
    let firstInvalid = null;

    Object.keys(errors).forEach((key) => {
      if (key === '_form') return;

      const message = this.form.querySelector(`[data-error-for="${key}"]`);
      const input = this.form.querySelector(`[name="${key}"]`);
      if (!message || !input) return;

      message.textContent = this.strings['field_' + key] || errors[key];
      input.setAttribute('aria-invalid', 'true');

      const field = input.closest('[data-contact-form-field]');
      if (field) field.classList.add('c-contact-form__field--invalid');

      if (!firstInvalid) firstInvalid = input;
    });

    // Anything we could not attach to a field still has to be visible somewhere.
    const unattached = Object.keys(errors).some(
      (key) => key === '_form' || !this.form.querySelector(`[data-error-for="${key}"]`)
    );
    if (unattached) this.showFormError(this.messageForStatus(status));

    if (firstInvalid) firstInvalid.focus();
  }

  messageForStatus(status) {
    if (status === 429) return this.strings.error_rate_limit;
    return this.strings.error_generic;
  }

  showFormError(message) {
    if (!this.formError) return;
    this.formError.textContent = message;
  }

  showSuccess() {
    if (!this.success) {
      this.showFormError(this.strings.error_generic);
      return;
    }

    this.form.hidden = true;
    this.success.hidden = false;
    this.success.focus();
  }

  resetCaptcha() {
    if (window.turnstile) window.turnstile.reset();
  }
}

function initContactForms() {
  document.querySelectorAll('[data-contact-form]').forEach((form) => {
    if (form.dataset.contactFormReady) return;
    form.dataset.contactFormReady = 'true';
    new ContactForm(form);
  });
}

initContactForms();
// The theme editor re-renders sections without reloading the page.
document.addEventListener('shopify:section:load', initContactForms);
